import type { Icon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx.js';

/**
 * A room off the home: a round chip with the room's name under it.
 *
 * The home poster opens Learn, Stats, Profile and Settings from a row of
 * these, drawn off the `lobby-home` sketch's exhibit A (Mitch, 2026-09-07).
 * Round means tappable on this screen: the heads are squared, so the circles
 * are the only round things a thumb can press.
 *
 * The chip is `Button`'s default face at door weight: white, a 2px ink stroke
 * and `shadow-sm shadow-ink`. The name sits under it on the ground, at 700
 * like a door label, and the whole column is the link, so the target is the
 * circle and its name together and well past 48px.
 *
 * It reacts the way every control does (BRAND § Every control reacts). The
 * hover washes the face with `wash-ink`, and the press moves the circle one
 * pixel toward its shadow while the shadow settles from 3px to 2px. Both are
 * `group-` variants, because the link is the column and the face is the
 * circle inside it. The name does not move: it is standing on the page.
 *
 * The mark is decorative and the name is the link's accessible name, so the
 * chip draws the icon itself, `aria-hidden` and at the chip's own size, rather
 * than taking a node a caller could forget to hide.
 */

export interface RoomChipProps extends Omit<LinkProps, 'className' | 'style' | 'children'> {
  /** The room's mark: a Phosphor icon component, drawn bold at the chip's size. */
  readonly icon: Icon;
  /** The room's name, already translated. */
  readonly label: ReactNode;
  /** Layout only. The chip's shape is not the caller's to change. */
  readonly className?: string | undefined;
}

const FACE =
  'flex size-16 items-center justify-center rounded-full border-2 border-ink bg-ground-light text-ink shadow-sm shadow-ink transition-control duration-150 ease-out group-hover:wash-ink group-active:translate-x-px group-active:translate-y-px group-active:shadow-xs tablet:size-19';

export function RoomChip({
  icon: Mark,
  label,
  className,
  ...rest
}: RoomChipProps): React.JSX.Element {
  return (
    <Link
      className={cx(
        'group flex flex-col items-center gap-2 rounded-2xl text-ink no-underline tablet:gap-3',
        className,
      )}
      {...rest}
    >
      <span className={FACE}>
        <Mark
          weight="bold"
          aria-hidden="true"
          focusable="false"
          className="size-7 flex-none tablet:size-8"
        />
      </span>
      <span className="text-sm leading-none font-bold">{label}</span>
    </Link>
  );
}
